/** @format */
import { CombinedGraphQLErrors, InMemoryCache } from '@apollo/client/core';
import { ApolloLink } from '@apollo/client/link';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { QueryRef } from 'apollo-angular';
import { HttpBatchLink } from 'apollo-angular/http';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

export const apolloFactory = (httpBatchLink: HttpBatchLink) => {
    const errorHandler = new ErrorLink(({ error }) => {
        if (CombinedGraphQLErrors.is(error)) {
            error.errors.forEach(({ message, locations, path }) => {
                console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
            });
        } else {
            console.error(`[Network error]: ${error.toString()}`);
        }
    });

    const retryLink = new RetryLink({
        delay: {
            initial: 500,
            max: Infinity,
            jitter: true,
        },
        attempts: {
            max: 5,
            retryIf: (error, _operation) => !!error,
        },
    });

    const batchLink = httpBatchLink.create({
        uri: '/api',
        batchInterval: 50,
        batchMax: 20,
    });

    const link = ApolloLink.from([retryLink, errorHandler, batchLink]);

    return {
        link,
        cache: new InMemoryCache({
            typePolicies: {
                OpenUniversityProduct: {
                    keyFields: ['id'],
                },
                CourseUnitRealisation: {
                    keyFields: ['id'],
                },
                CourseUnit: {
                    keyFields: ['id'],
                },
                Query: {
                    fields: {
                        search_course_unit_realisations_for_teacher: {
                            keyArgs: [
                                'activityStatus',
                                'flowStates',
                                'enrolmentOngoing',
                                'evaluationPeriod',
                                'pastArchivedAndCancelled',
                                'fullTextQuery',
                                'activityEnd',
                                'uiLang',
                                'continuousEnrolment',
                                'codeUrns',
                                'fetchOnlyHiddenCurs',
                            ],
                            merge(existing, incoming, { args: { start = 0 } }) {
                                if (!incoming) {
                                    return existing;
                                }
                                if (!existing) {
                                    return incoming;
                                }
                                const merged = existing.searchResults ? existing.searchResults.slice(0) : [];
                                for (let i = 0; i < incoming.searchResults.length; i += 1) {
                                    merged[start + i] = incoming.searchResults[i];
                                }
                                return { ...incoming, searchResults: merged.slice(0, start + incoming.searchResults.length) };
                            },
                        },
                    },
                },
            },
        }),
        defaultOptions: {
            watchQuery: {
                errorPolicy: 'all',
            },
        },
    };
};

export const queryOptions = {
    nextFetchPolicy: 'cache-first' as const,
    fetchPolicy: 'cache-first' as const,
    returnPartialData: false,
};

export interface LoadingAndError {
    loading: Observable<boolean>;
    errors: Observable<any>;
}

export const initLoadingAndError = (observable: QueryRef<any, any>): LoadingAndError => ({
    loading: observable.valueChanges.pipe(
        startWith({ loading: true }),
        map(({ loading }) => loading),
        catchError(_ => of(false)),
    ),
    errors: observable.valueChanges.pipe(
        startWith({ error: undefined }),
        map(({ error }) => error),
        catchError(error => of(error)),
    ),
});

export const catchErrorHandler = <T>() =>
    catchError<T, Observable<T>>(error => {
        console.error(error);
        return EMPTY;
    });
